import type { SerializedResponseError } from './SerializedReponseError';

// Mirrors the backend's per-file multer limit (SIZE_LIMIT in homemd-server
// src/app/middleware.js). Checking before the request means an oversized file
// is rejected instantly — otherwise the whole file is uploaded first and only
// then refused with a 413, which took over a minute for an 86 MB PDF.
export const MAX_UPLOAD_BYTES = 30 * 1024 * 1024;

const OVERSIZED_UPLOAD_ERROR = 'OversizedUploadError';

// Decimal MB, to match the size macOS Finder shows staff for the same file.
// Anything rejected here is > 31.4 decimal MB, so it still reads as "over 30 MB".
const toMb = (bytes: number) => (bytes / 1000 / 1000).toFixed(1).replace(/\.0$/, '');
const MAX_UPLOAD_MB_LABEL = '30';

/** Returns the first file in the request params that the backend would reject as too large. */
export const findOversizedFile = (params: unknown): Blob | undefined => {
  if (typeof Blob === 'undefined' || !params || typeof params !== 'object') return undefined;
  for (const value of Object.values(params)) {
    const candidates = Array.isArray(value) ? value : [value];
    const oversized = candidates.find((it) => it instanceof Blob && it.size > MAX_UPLOAD_BYTES);
    if (oversized) return oversized;
  }
  return undefined;
};

/** Shaped like a serialized 413 from the backend so existing error handling keeps working. */
export const oversizedFileError = (file: Blob): SerializedResponseError => {
  const name = typeof File !== 'undefined' && file instanceof File ? `"${file.name}" ` : '';
  const message =
    `The file ${name}is ${toMb(file.size)} MB, over the ${MAX_UPLOAD_MB_LABEL} MB upload limit. ` +
    'Please compress it or split it into smaller files and upload those instead.';
  return {
    isResponseError: true,
    response: {
      headers: {},
      status: 413,
      jsonBody: { message },
      usesBody: true,
      jsonBodyMessage: message,
    },
    message,
    name: OVERSIZED_UPLOAD_ERROR,
  };
};

export const isOversizedUploadError = (error: unknown): error is SerializedResponseError =>
  (error as SerializedResponseError | undefined)?.name === OVERSIZED_UPLOAD_ERROR;
